/**
 * JobCriteriaEditor - View and edit screening criteria for a job
 *
 * Features:
 * - View current screening criteria text
 * - Edit criteria with a textarea
 * - Sync criteria from SmartRecruiters
 * - View changelog of past criteria versions
 */

import { useState } from 'react';
import {
  useJobCriteria,
  useJobCriteriaChangelog,
  useUpdateJobCriteria,
  useSyncJobCriteria,
  useJob,
} from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import {
  FileText,
  Save,
  RefreshCw,
  History,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Info,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ScreeningCriteriaWizard } from './jobs/ScreeningCriteriaWizard';

interface JobCriteriaEditorProps {
  jobId: number;
  srJobId: string | null;
}

export function JobCriteriaEditor({ jobId, srJobId }: JobCriteriaEditorProps) {
  const { showToast } = useToast();
  const { data: criteria, isLoading } = useJobCriteria(jobId);
  const { data: changelogData } = useJobCriteriaChangelog(jobId);
  const { data: job } = useJob(jobId);
  const updateMutation = useUpdateJobCriteria();
  const syncMutation = useSyncJobCriteria();

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [showChangelog, setShowChangelog] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const handleStartEditing = () => {
    setEditText(criteria?.screeningCriteriaText || '');
    setChangeReason('');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editText.trim()) {
      showToast('Criteria text cannot be empty', 'error');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        jobId,
        criteriaText: editText.trim(),
        changeReason: changeReason.trim() || undefined,
      });
      showToast('Screening criteria updated', 'success');
      setIsEditing(false);
    } catch (err) {
      const error = err as Error;
      showToast(`Failed to save: ${error.message}`, 'error');
    }
  };

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync(jobId);
      showToast('Criteria sync queued - check back in a moment', 'success');
    } catch (err) {
      const error = err as Error;
      showToast(`Sync failed: ${error.message}`, 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  const hasCriteria = !!criteria?.screeningCriteriaText;

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">
            Screening Criteria
          </h4>
          {criteria?.screeningCriteriaVersion && (
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
              v{criteria.screeningCriteriaVersion}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {srJobId && (
            <button
              onClick={handleSync}
              disabled={syncMutation.isPending}
              className="btn btn-secondary text-sm"
              title="Pull latest criteria from SmartRecruiters job notes and scorecards"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {syncMutation.isPending ? 'Syncing...' : 'Sync from SR'}
            </button>
          )}
          {!isEditing && (
            <button
              onClick={() => setWizardOpen(true)}
              className="btn btn-secondary text-sm"
              title="Use Arbie to generate screening criteria from the job description"
              data-testid="generate-criteria-btn"
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Generate Criteria
            </button>
          )}
          {!isEditing && (
            <button onClick={handleStartEditing} className="btn btn-primary text-sm">
              {hasCriteria ? 'Edit' : 'Add Criteria'}
            </button>
          )}
        </div>
      </div>

      {/* SR sync notice — criteria is local-only, does not write back to SR */}
      {srJobId && (
        <div className="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
          <Info className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Criteria generated or edited here is used by Arbie for AI scoring only.
            It does <strong>not</strong> sync back to SmartRecruiters.
          </p>
        </div>
      )}

      {/* Screening Criteria Wizard */}
      {job && (
        <ScreeningCriteriaWizard
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          job={{
            id: job.id,
            title: job.title,
            description: job.description,
            existingCriteriaVersion: criteria?.screeningCriteriaVersion ?? null,
          }}
        />
      )}

      {/* Error display */}
      {criteria?.screeningCriteriaError && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
          <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Last sync error</p>
            <p className="text-sm text-red-600 dark:text-red-400">{criteria.screeningCriteriaError}</p>
          </div>
        </div>
      )}

      {/* Criteria display or editor */}
      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder="Enter screening criteria for this job position...&#10;&#10;Example:&#10;- Must have: 5+ years Python experience&#10;- Must have: AWS certification&#10;- Preferred: Machine learning background&#10;- Knockout: Cannot require visa sponsorship"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 min-h-[200px] font-mono"
            rows={10}
          />
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{editText.length}/10,000 characters</span>
            {criteria?.screeningCriteriaSource && (
              <span>| Source: {criteria.screeningCriteriaSource}</span>
            )}
          </div>
          <input
            type="text"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            placeholder="Change reason (optional)"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending || !editText.trim()}
              className="btn btn-primary text-sm"
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {updateMutation.isPending ? 'Saving...' : 'Save Criteria'}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : hasCriteria ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-4">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-100 font-mono">
            {criteria.screeningCriteriaText}
          </pre>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-300 border-t border-gray-200 dark:border-gray-600 pt-3">
            {criteria.screeningCriteriaSource && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Source: {criteria.screeningCriteriaSource}
              </span>
            )}
            {criteria.screeningCriteriaSyncedAt && (
              <span>
                Last synced: {formatDistanceToNow(new Date(criteria.screeningCriteriaSyncedAt), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-center">
          <FileText className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No screening criteria configured for this job
          </p>
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            Add criteria manually or sync from SmartRecruiters job notes
          </p>
        </div>
      )}

      {/* Changelog section */}
      {changelogData && changelogData.data.length > 0 && (
        <div>
          <button
            onClick={() => setShowChangelog(!showChangelog)}
            className="flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            {showChangelog ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <History className="h-4 w-4" />
            Change History ({changelogData.data.length})
          </button>

          {showChangelog && (
            <div className="mt-2 space-y-2">
              {changelogData.data.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
                >
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 font-medium">
                        v{entry.version}
                      </span>
                      <span>by {entry.changedBy}</span>
                    </div>
                    <span title={format(new Date(entry.createdAt), 'PPpp')}>
                      {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  {entry.changeReason && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {entry.changeReason}
                    </p>
                  )}
                  {entry.criteriaText && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                        View criteria text
                      </summary>
                      <pre className="mt-1 whitespace-pre-wrap rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs text-gray-600 dark:text-gray-200 font-mono max-h-40 overflow-y-auto">
                        {entry.criteriaText}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
